<template>
  <label class="skif-checkbox-label" data-testid="components-ui-checkbox-checkbox-label">
    <input
      class="skif-checkbox-input"
      type="checkbox"
      :checked="modelValue || isCheckedAll"
      :disabled="isDisabled"
      :indeterminate.prop="isIndeterminate"
      @change="handleChange"
     data-testid="components-ui-checkbox-checkbox-input">

    <div
      class="skif-checkbox-wrapper"
      :class="[
        { 'skif-checkbox-wrapper--is-checked': modelValue || isCheckedAll },
        { 'skif-checkbox-wrapper--is-disabled': isDisabled },
        { 'skif-checkbox-wrapper--is-indeterminate': isIndeterminate },
        { 'skif-checkbox-wrapper--is-error': isError }
      ]"
     data-testid="components-ui-checkbox-checkbox-div">
      <arrow-success-icon
        v-if="(modelValue || isCheckedAll) && !isIndeterminate"
        color="white"
      data-testid="components-ui-checkbox-checkbox-arrow-success-icon" />

      <indeterminate-icon v-if="isIndeterminate" data-testid="components-ui-checkbox-checkbox-indeterminate-icon" />
    </div>

    <span
      v-if="label"
      class="skif-checkbox-label__text"
     data-testid="components-ui-checkbox-checkbox-span">
      {{ label }}
    </span>
  </label>
</template>

<script setup>
import ArrowSuccessIcon from '@/components/ui/Checkbox/assets/ArrowSuccess.vue'
import IndeterminateIcon from '@/components/ui/Checkbox/assets/AllSelect.vue'

// В Vue 3 props объявляются через defineProps
const props = defineProps({
  modelValue: { type: Boolean, default: false },
  label: { type: String, default: '' },
  isDisabled: { type: Boolean, default: false },
  isIndeterminate: { type: Boolean, default: false },
  isError: { type: Boolean, default: false },
  isCheckedAll: { type: Boolean, default: false },
  // uniqId больше не используется в шаблоне, но мы оставляем его в props,
  // чтобы родительские компоненты, где он прописан, не выдавали предупреждений в консоль
  uniqId: { type: [Number, String], default: '' }
})

// Объявляем события
const emit = defineEmits(['update:modelValue', 'change'])

// Обработчик изменения
const handleChange = (event) => {
  const checked = event.target.checked
  emit('update:modelValue', checked)
  emit('change', checked)
}
</script>

<style lang="scss" scoped>
$red-tone-1: #d45050;
$blue-tone-2: #3a7cca;
$grey-tone-2: #a6aeb8;
$grey-tone-3: #828d9b;
$light-grey-tone-1: #edeff3;
$light-blue-tone-2: #dbebff;

.skif-checkbox-label {
  display: inline-flex; // Изменено на inline-flex для лучшего поведения в потоке
  align-items: center;
  cursor: pointer;

  // Если чекбокс заблокирован, отключаем клики по всему label
  &:has(.skif-checkbox-input:disabled) {
    cursor: not-allowed;
  }

  &__text {
    margin-left: 8px;
    line-height: 19px;
    color: rgba(84, 119, 169, var(--text-opacity));
    --text-opacity: 1;
    font-weight: 700;
    font-size: 14px;
    user-select: none; // Чтобы текст не выделялся при быстрых кликах
  }
}

.skif-checkbox-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid $grey-tone-3;
  transition: all 0.2s ease;
  flex-shrink: 0; // Чтобы квадрат не сплющивался, если текст длинный

  &:hover {
    border-color: $blue-tone-2;
  }

  &--is-checked {
    background-color: $blue-tone-2;
    border-color: $blue-tone-2;

    &:hover {
      background-color: $blue-tone-2;
    }
  }

  &--is-disabled {
    border-color: $grey-tone-2;
    background-color: $light-grey-tone-1;
    &:hover {
      border-color: $grey-tone-2;
      background-color: $light-grey-tone-1;
    }
  }

  &--is-indeterminate {
    background-color: $blue-tone-2;
    border-color: $blue-tone-2;
    &:hover {
      background-color: $blue-tone-2;
      :deep(svg) {
        border-radius: 2px;
        stroke: $light-blue-tone-2;
      }
    }
  }

  &--is-error {
    border-color: $red-tone-1;
  }

  &--is-indeterminate.skif-checkbox-wrapper--is-error {
    background-color: $red-tone-1;
    &:hover {
      border-color: $red-tone-1;
      :deep(svg) {
        border-radius: 2px;
        stroke: $light-blue-tone-2;
      }
    }
  }
}

/* ПРАВИЛЬНОЕ скрытие нативного чекбокса (A11Y).
  Заменяет display: none; 
  Теперь чекбокс не виден, но на него можно навести фокус с помощью Tab!
*/
.skif-checkbox-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Бонус: красивый фокус при навигации с клавиатуры (Tab) */
.skif-checkbox-input:focus-visible + .skif-checkbox-wrapper {
  outline: 2px solid $blue-tone-2;
  outline-offset: 2px;
}
</style>
